import styled from "@emotion/styled";
import { ArrowRight, MoreVert } from "@mui/icons-material";
import {
  Breadcrumbs,
  Checkbox,
  Menu,
  MenuItem,
  Popover,
  Stack,
  Typography
} from "@mui/material";
import { LoadingScreen } from "components/LoadingScreen";
import { Button, DeleteModal } from "components/UI";
import { useChurchStore } from "features/churches";
import { capitalize, startCase } from "lodash";
import { useEffect, useState } from "react";

import moment from "moment";
import { useNavigate, useParams } from "react-router-dom";
import { useCentralStore } from "stores";
import {
  useChartOfAccounts,
  useDeleteRequisition,
  useRequisition
} from "../api";
import { MakeVoidModal } from "../components";
import { RequisitionRequestForm } from "../components/Forms/RequisitionRequestForm";
import { statusColors, statusStyleTypes } from "../constants/statuses";

const Title = styled.h6`
  margin: 0;
  font-size: 12px;
  font-weight: ${({ bold }) => (bold ? 600 : 400)};
  cursor: pointer;
`;

const Status = styled.div`
  border-radius: 2px;
  background: ${({ color }) => color};
  color: #ffffff;
  padding: 4px 8px;
  text-transform: capitalize;
  font-size: 10px;
`;

const StyledAlert = styled.div`
  padding: 16px;
  background-color: ${props => props.bg};
  display: flex;
  align-items: center;
  gap: 16px;
  border-radius: 20px;
  flex: 1;

  .startIcon {
    min-height: 48px;
    min-width: 48px;
    background-color: ${props => props.iconBg};
    display: grid;
    place-items: center;
    font-size: 24px;
    border-radius: 100%;
    color: #ffffff;
  }

  div {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
  }

  .endIcon {
    min-height: 24px;
    min-width: 24px;
    background-color: #ffffff;
    display: grid;
    place-items: center;
    border-radius: 100%;
    margin-left: auto;
    cursor: pointer;
  }

  h3 {
    margin: 0;
  }

  p {
    margin: 0;
  }
`;

export const RequisitionDetails = () => {
  const [requisition, setRequisition] = useState(null);
  const [isEditMode, setIsEditMode] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [openMakeVoidModal, setOpenMakeVoidModal] = useState(false);

  const handleClick = event => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const open = Boolean(anchorEl);

  const { id } = useParams();
  const selectedChurch = useChurchStore(state => state.selectedChurch);
  const deleteRequisitionMutation = useDeleteRequisition();
  const openDeleteModal = useCentralStore(state => state.openDeleteModal);
  const navigate = useNavigate();

  const { data, isLoading } = useRequisition({
    churchId: selectedChurch?.id,
    id
  });

  const chartOfAccountsQuery = useChartOfAccounts({
    churchId: selectedChurch?.id,
    filters: [
      { column: "account_type", operator: "in", value: ["EXPENSE", "ASSET"] }
    ]
  });

  useEffect(() => {
    if (data) {
      setRequisition(data.result);
    }
  }, [data]);

  if (isLoading || chartOfAccountsQuery.isLoading) {
    return <LoadingScreen />;
  }

  return (
    <Stack spacing={3}>
      <Breadcrumbs
        separator={<ArrowRight fontSize="small" />}
        aria-label="breadcrumb"
      >
        <Title onClick={() => navigate("/finances/temp/expense")}>
          Expense
        </Title>
        <Title onClick={() => navigate("/finances/temp/expense/requisition")}>
          Requisition
        </Title>
        <Title bold>Request</Title>
      </Breadcrumbs>

      {requisition?.requisition_status === "PAID_IN_FULL" && (
        <Stack
          direction="row"
          justifyContent="space-between"
          spacing={4}
          sx={{ bgcolor: "#F8F8F8", p: 3, borderRadius: 1 }}
        >
          <Stack>
            <Typography variant="subtitle2">Paid in full</Typography>
            <Typography variant="caption">This request is paid</Typography>
          </Stack>
          <Button onClick={() => setOpenMakeVoidModal(true)}>Make Void</Button>
        </Stack>
      )}

      <Stack direction="row" justifyContent="space-between" spacing={4}>
        <Stack
          direction="row"
          justifyContent="space-between"
          sx={{
            backgroundColor: "#FAFAFA",
            borderRadius: 1,
            p: 2,
            width: "900px"
          }}
        >
          <Stack>
            <Typography variant="caption">Status</Typography>
            <Status color={statusColors[requisition?.requisition_status]}>
              {startCase(capitalize(requisition?.requisition_status))}
            </Status>
          </Stack>
          <Stack>
            <Typography variant="caption">Available Budget</Typography>
            <Typography fontWeight="bold">
              {Number(requisition?.amount).toLocaleString()} ETB
            </Typography>
          </Stack>
          <Stack>
            <Typography variant="caption">Current Approver</Typography>
            <Stack>
              {requisition?.approvers?.map(approver => (
                <Stack direction="row" alignItems="center" key={approver.id}>
                  <Typography fontWeight="bold">
                    {approver.approver.first_name} {approver.approver.last_name}
                  </Typography>
                  <Checkbox
                    checked={approver.pivot.status === "APPROVED"}
                    readOnly
                  />
                </Stack>
              ))}
            </Stack>
          </Stack>
        </Stack>
        {(requisition?.requisition_status === "DRAFTED" ||
          requisition?.requisition_status === "SCHEDULED") && (
          <StyledAlert
            bg={
              statusStyleTypes[
                requisition?.requisition_status?.toLowerCase()
              ]?.["backgroundColor"]
            }
            iconBg={
              statusStyleTypes[
                requisition?.requisition_status?.toLowerCase()
              ]?.["iconBackgroundColor"]
            }
          >
            <span className="startIcon">
              {
                statusStyleTypes[
                  requisition?.requisition_status?.toLowerCase()
                ]?.["icon"]
              }
            </span>
            <div>
              <h3>
                {
                  statusStyleTypes[
                    requisition?.requisition_status?.toLowerCase()
                  ]?.["title"]
                }
              </h3>
              <p>
                {requisition?.requisition_status?.toLowerCase() === "scheduled"
                  ? `This payment is scheduled to be paid on ${moment(
                      requisition?.schedule_at
                    ).format("DD/MM/YYYY")}.`
                  : statusStyleTypes[
                      requisition?.requisition_status?.toLowerCase()
                    ]?.["message"]}
              </p>
            </div>
          </StyledAlert>
        )}
      </Stack>

      {requisition?.requisition_status !== "PAID_IN_FULL" && (
        <Stack direction="row" alignItems="center" spacing={1}>
          <Button onClick={() => setIsEditMode(!isEditMode)}>
            {isEditMode ? "Cancel" : "Edit"}
          </Button>
          <Button
            flavor="text"
            onClick={handleClick}
            endIcon={<MoreVert fontSize="small" />}
          >
            Action
          </Button>
        </Stack>
      )}

      {requisition && (
        <RequisitionRequestForm
          isView={!isEditMode}
          isEditMode={isEditMode}
          data={requisition}
        />
      )}

      <DeleteModal
        name={requisition?.reason}
        onDelete={() => {
          deleteRequisitionMutation.mutate({
            churchId: selectedChurch?.id,
            requisitionId: requisition?.id
          });
        }}
      />

      <MakeVoidModal
        requisitionId={requisition?.id}
        open={openMakeVoidModal}
        onClose={() => setOpenMakeVoidModal(false)}
      />

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "left"
        }}
      >
        <Menu anchorEl={anchorEl} open={open} onClose={handleClose}>
          <MenuItem onClick={openDeleteModal}>
            <Typography variant="body2">Delete</Typography>
          </MenuItem>
        </Menu>
      </Popover>
    </Stack>
  );
};
